/**
 * Impact at the flash-flood site: what the computed flood reaches, from the run's own
 * rasters and the OpenStreetMap layers fetched for the site (GET /api/runs/{id}/exposure,
 * backend/app/exposure.py).
 *
 * Same vocabulary as the dam-failure page: a stat row, the map, category cards, the
 * settlement table. A category with nothing in the flood extent is not rendered, and
 * a settlement with no census record has no population.
 */

import { useMemo } from 'react'
import { Marker } from 'react-map-gl/maplibre'
import type { LayerSpecification, SourceSpecification } from 'maplibre-gl'

import { BaseMap, type Bounds } from '../../components/BaseMap'
import { DebrisQuantities, type DebrisCase } from '../../components/DebrisQuantities'
import { HAZARD, HAZARD_LABELS } from '../../components/Legend'
import { C } from '../../design/tokens'

type Geo = { type: string; coordinates: unknown }

interface Quantity {
  key: string
  label: string
  value: number
  unit: string
  formula: string
  inputs: { name: string; value: number; unit?: string; source: string; note?: string }[]
}

export interface ExposurePayload {
  run_id: string
  site_id: string
  available: boolean
  attribution?: string
  osm_fetched_utc?: string
  wet_threshold_m?: number
  cell_m?: number
  summary?: { inundated_area_km2: number; excluded_area_km2: number; max_depth_m: number | null; max_velocity_ms: number | null }
  hazard_km2?: Record<string, number>
  exclusion_rule?: string
  footprint_lonlat?: Geo | null
  districts?: { name: string; osm_id: number; inundated_km2: number }[]
  categories?: {
    key: string
    label: string
    source: string
    count?: number
    total_km?: number
    by_hazard?: Record<string, number>
    by_class_km?: Record<string, number>
    features?: { name: string | null; osm_id: number; carries?: string; kind?: string; geometry: Geo }[]
  }[]
  settlements?: {
    name: string | null
    place: string
    lon: number
    lat: number
    osm_id: number
    max_depth_m?: number
    max_velocity_ms?: number
    hazard_class?: number
    arrival_min?: number
    excluded?: boolean
    population?: string
    population_source?: string
  }[]
  crossings?: {
    osm_id: number
    name: string
    type: string
    lon: number
    lat: number
    arrival_time_min: number | null
    max_depth_m: number | null
    max_velocity_ms: number | null
    hazard_class: number | null
    wet_cells: number
  }[]
  deep_ponding?: {
    cells: number
    area_km2: number
    max_apparent_depth_m: number | null
    void_cells_wet: number
    void_area_km2: number
    rule: string
    source: string
  }
  debris?: { quantities: Quantity[]; derived?: DebrisCase | null; sediment: { value: string; on: boolean; source: string } | null; method: string | null }
}

const n = (v: number, d = 1) => v.toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d })
const hm = (minutes: number) => `${Math.floor(minutes / 60)} h ${String(Math.round(minutes % 60)).padStart(2, '0')} min`

function walk(coords: unknown, out: number[]) {
  if (Array.isArray(coords) && typeof coords[0] === 'number') {
    const [x, y] = coords as number[]
    out[0] = Math.min(out[0], x)
    out[1] = Math.min(out[1], y)
    out[2] = Math.max(out[2], x)
    out[3] = Math.max(out[3], y)
  } else if (Array.isArray(coords)) coords.forEach((c) => walk(c, out))
}

function Tile({ label, value, unit, note, testid }: { label: string; value: string; unit?: string; note: string; testid?: string }) {
  return (
    <div className="metric-tile" data-testid={testid}>
      <span className="section-label">{label}</span>
      <span className="key-figure">
        {value}
        {unit && <span className="unit">{unit}</span>}
      </span>
      <span className="metric-note">{note}</span>
    </div>
  )
}

function Card({ title, note, children, testid }: { title: string; note: string; children: React.ReactNode; testid: string }) {
  return (
    <section className="panel" style={{ padding: 0 }} data-testid={testid}>
      <header className="panel-header">
        <span className="panel-title" style={{ fontSize: 'var(--fs-body)', fontWeight: 600 }}>
          {title}
        </span>
      </header>
      <div className="p-4 text-14">{children}</div>
      <div className="border-t border-[var(--ink-border)] px-4 py-2 text-12 text-[var(--ink-text-faint)]">{note}</div>
    </section>
  )
}

export function SiteExposure({ data }: { data: ExposurePayload }) {
  const s = data.summary!
  const cats = Object.fromEntries((data.categories ?? []).map((c) => [c.key, c]))
  const runNote = `run ${data.run_id}, ${n(data.cell_m ?? 0, 0)} m cells, depth > ${data.wet_threshold_m} m`
  const hazardTotal = Object.values(data.hazard_km2 ?? {}).reduce((a, b) => a + b, 0)

  const map = useMemo(() => {
    const box = [Infinity, Infinity, -Infinity, -Infinity]
    if (data.footprint_lonlat) walk(data.footprint_lonlat.coordinates, box)
    ;(data.settlements ?? []).forEach((p) => walk([p.lon, p.lat], box))
    ;(data.crossings ?? []).forEach((p) => walk([p.lon, p.lat], box))
    if (!Number.isFinite(box[0])) return null
    const sources: Record<string, SourceSpecification> = {}
    const layers: LayerSpecification[] = []
    if (data.footprint_lonlat) {
      sources.footprint = { type: 'geojson', data: data.footprint_lonlat as GeoJSON.Geometry }
      layers.push({ id: 'footprint-fill', type: 'fill', source: 'footprint', paint: { 'fill-color': HAZARD[3], 'fill-opacity': 0.4 } })
      layers.push({ id: 'footprint-line', type: 'line', source: 'footprint', paint: { 'line-color': HAZARD[2], 'line-width': 1 } })
    }
    const bridges = cats.bridges?.features ?? []
    if (bridges.length) {
      sources.bridges = {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: bridges.map((b) => ({ type: 'Feature', properties: {}, geometry: b.geometry })) } as never,
      }
      layers.push({ id: 'bridges', type: 'line', source: 'bridges', paint: { 'line-color': C.text, 'line-width': 4 } })
    }
    const water = cats.water_infra?.features ?? []
    if (water.length) {
      sources.water = {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: water.map((b) => ({ type: 'Feature', properties: {}, geometry: b.geometry })) } as never,
      }
      layers.push({ id: 'water-infra', type: 'circle', source: 'water', paint: { 'circle-color': C.mapRiver, 'circle-radius': 6, 'circle-stroke-color': C.bg, 'circle-stroke-width': 1.5 } })
      layers.push({ id: 'water-infra-line', type: 'line', source: 'water', paint: { 'line-color': C.mapRiver, 'line-width': 3 } })
    }
    return { bounds: box as Bounds, sources, layers }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.run_id])

  const crossings = data.crossings ?? []
  const firstCrossing = crossings.find((c) => c.arrival_time_min !== null) ?? null
  const hazardColour = (c: number | null) => (c ? HAZARD[c] : 'var(--ink-text-faint)')
  const reached = crossings.filter((c) => c.arrival_time_min !== null)
  const topH = reached.length ? Math.max(...reached.map((c) => (c.arrival_time_min as number) / 60)) : 0
  const step = [0.5, 1, 2, 3, 6, 12, 24].find((v) => topH / v <= 5) ?? 48
  const chartTop = Math.ceil(topH / step) * step || step
  const infra: { key: string; category: string; detail: string; value: string; measure: string; named: string }[] = []
  if (cats.buildings) {
    for (const k of ['4', '3', '2', '1', 'excluded']) {
      const v = cats.buildings.by_hazard?.[k]
      if (v) {
        infra.push({ key: `b${k}`, category: 'Buildings', detail: k === 'excluded' ? 'on data-quality cells' : `hazard ${k} · ${HAZARD_LABELS[Number(k)]}`,
          value: String(v), measure: 'count', named: '' })
      }
    }
  }
  if (cats.roads) {
    for (const [cls, km] of Object.entries(cats.roads.by_class_km ?? {})) {
      infra.push({ key: `r${cls}`, category: 'Roads', detail: cls.replace(/_/g, ' '), value: n(km, 2), measure: 'km inundated', named: '' })
    }
  }
  if (cats.bridges) {
    const names = (cats.bridges.features ?? []).map((b) => b.name).filter(Boolean) as string[]
    infra.push({ key: 'bridges', category: 'Bridges and river crossings', detail: 'in the flood extent', value: String(cats.bridges.count),
      measure: 'count', named: names.join(', ') })
  }
  if (cats.water_infra) {
    const names = (cats.water_infra.features ?? []).map((b) => b.name).filter(Boolean) as string[]
    infra.push({ key: 'water', category: 'Hydropower and water infrastructure', detail: 'in the flood extent', value: String(cats.water_infra.count),
      measure: 'count', named: names.join(', ') })
  }

  return (
    <div className="flex flex-1 flex-col gap-4 p-5" data-testid="site-exposure">
      {/* TOP: the stat row, each with its source */}
      <div className="grid shrink-0 grid-cols-2 gap-4 xl:grid-cols-4">
        <Tile label="Inundated area" value={n(s.inundated_area_km2, 1)} unit="km²" note={`Source: ${runNote}`} testid="tile-area" />
        {firstCrossing && (
          <Tile
            label="Shortest arrival"
            value={n((firstCrossing.arrival_time_min as number) / 60, 1)}
            unit="h"
            note={`Source: run arrival time, at ${firstCrossing.name}`}
          />
        )}
        {crossings.length > 0 && (
          <Tile label="Crossings and roads reached" value={String(crossings.length)} note="Source: OSM bridge and highway ways on wet cells" />
        )}
        {s.max_depth_m !== null && (
          <Tile label="Peak depth" value={n(s.max_depth_m, 1)} unit="m" note="Source: run maximum depth, data-quality cells excluded" />
        )}
      </div>

      {/* MAP: the full-width row, as on the dam-failure page */}
      {map && (
        <section className="panel flex flex-col" style={{ padding: 0 }}>
          <div className="panel-header">
            <span className="panel-title" style={{ fontSize: 'var(--fs-body)', fontWeight: 600 }}>
              Flood footprint
            </span>
            <span className="section-label">crossings coloured by hazard class</span>
          </div>
          <div className="relative" style={{ height: 'clamp(460px, 64vh, 780px)' }} data-testid="impact-map">
            <BaseMap bounds={map.bounds} runId={data.run_id} sources={map.sources} layers={map.layers} padding={40} dragRotate={false} maxPitch={0}>
              {crossings.map((c) => (
                <Marker key={c.osm_id} longitude={c.lon} latitude={c.lat} anchor="center" style={{ pointerEvents: 'none' }}>
                  <span className="block h-2.5 w-2.5" style={{ background: hazardColour(c.hazard_class), border: `1.5px solid ${C.bg}` }} />
                </Marker>
              ))}
              {(data.settlements ?? []).map((p) => (
                <Marker key={p.osm_id} longitude={p.lon} latitude={p.lat} anchor="center">
                  <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: C.text, border: `1.5px solid ${C.bg}` }} />
                </Marker>
              ))}
            </BaseMap>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-3 border-t px-4 py-2" style={{ borderColor: 'var(--ink-border)' }}>
            <span className="section-label">Hazard</span>
            {[1, 2, 3, 4].map((c) => (
              <span key={c} className="flex items-center gap-1" style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}>
                <span style={{ width: 10, height: 10, background: HAZARD[c], display: 'inline-block' }} />
                {c}
              </span>
            ))}
            {cats.bridges && (
              <span className="flex items-center gap-1" style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}>
                <svg width="18" height="10">
                  <path d="M2 5H16" stroke="var(--ink-text)" strokeWidth="4" />
                </svg>
                Bridge
              </span>
            )}
            <span style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}>© {data.attribution}</span>
          </div>
        </section>
      )}

      {/* TABLE · WARNING WINDOW */}
      {crossings.length > 0 && (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)' }}>
          <section className="panel overflow-auto" style={{ padding: 0 }} data-testid="exposure-crossings">
            <div className="panel-header">
              <span className="panel-title" style={{ fontSize: 'var(--fs-body)', fontWeight: 600 }}>
                Crossings and roads
              </span>
              <span className="section-label">sorted by arrival</span>
            </div>
            <table className="data-table w-full">
              <thead>
                <tr>
                  <th>Location</th>
                  <th style={{ textAlign: 'right' }}>Arrival (h)</th>
                  <th style={{ textAlign: 'right' }}>Depth (m)</th>
                  <th style={{ textAlign: 'right' }}>Vel (m/s)</th>
                  <th>Hazard</th>
                </tr>
              </thead>
              <tbody>
                {crossings.map((c) => (
                  <tr key={c.osm_id}>
                    <td style={{ borderLeft: `3px solid ${hazardColour(c.hazard_class)}` }}>
                      <div style={{ color: 'var(--ink-text)' }}>{c.name}</div>
                      <div className="cell-note">{c.type} · {c.wet_cells} wet cells</div>
                    </td>
                    <td className="num">{c.arrival_time_min === null ? '' : n(c.arrival_time_min / 60, 2)}</td>
                    <td className="num">{c.max_depth_m === null ? '' : n(c.max_depth_m, 1)}</td>
                    <td className="num">{c.max_velocity_ms === null ? '' : n(c.max_velocity_ms, 2)}</td>
                    <td>
                      {c.hazard_class !== null && (
                        <span className="hazard-chip" style={{ border: `1px solid ${hazardColour(c.hazard_class)}`, color: hazardColour(c.hazard_class) }}>
                          {c.hazard_class}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          {reached.length > 0 && (
            <section className="panel flex flex-col" style={{ padding: 0 }} data-testid="exposure-warning-window">
              <div className="panel-header">
                <span className="panel-title" style={{ fontSize: 'var(--fs-body)', fontWeight: 600 }}>
                  Warning window
                </span>
                <span className="section-label">hours from breach</span>
              </div>
              <div className="p-4">
                <svg viewBox={`0 0 420 ${reached.length * 30 + 30}`} width="100%" role="img" aria-label="Arrival time at each crossing">
                  {[0, 0.25, 0.5, 0.75, 1].map((f) => (
                    <g key={f}>
                      <line x1={170 + f * 200} x2={170 + f * 200} y1={0} y2={reached.length * 30 + 6} stroke="var(--ink-border)" />
                      <text x={170 + f * 200} y={reached.length * 30 + 22} textAnchor="middle" fill="var(--ink-text-dim)" style={{ fontSize: 'var(--fs-11)', fontFamily: 'var(--font-mono)' }}>
                        {n(chartTop * f, chartTop < 2 ? 2 : 1)}
                      </text>
                    </g>
                  ))}
                  {reached.map((c, i) => {
                    const h = (c.arrival_time_min as number) / 60
                    return (
                      <g key={c.osm_id}>
                        <text x={162} y={i * 30 + 16} textAnchor="end" fill="var(--ink-text)" style={{ fontSize: 'var(--fs-11)' }}>
                          {c.name.length > 26 ? `${c.name.slice(0, 25)}…` : c.name}
                        </text>
                        <rect x={170} y={i * 30 + 6} width={Math.max(2, (h / chartTop) * 200)} height={14} fill={hazardColour(c.hazard_class)} fillOpacity={0.8} />
                        <text x={176 + (h / chartTop) * 200} y={i * 30 + 17} fill="var(--ink-text)" style={{ fontSize: 'var(--fs-11)', fontFamily: 'var(--font-mono)' }}>
                          {n(h, 2)} h
                        </text>
                      </g>
                    )
                  })}
                </svg>
              </div>
            </section>
          )}
        </div>
      )}

      {/* INFRASTRUCTURE: the same table as the dam-failure page */}
      {infra.length > 0 && (
        <section className="panel shrink-0" style={{ padding: 0 }} data-testid="exposure-infrastructure">
          <div className="panel-header">
            <span className="panel-title" style={{ fontSize: 'var(--fs-body)', fontWeight: 600 }}>
              Infrastructure in the footprint
            </span>
            <span className="section-label">
              {data.attribution}
              {data.osm_fetched_utc ? ` · extract ${data.osm_fetched_utc.slice(0, 10)}` : ''}
            </span>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Class</th>
                <th style={{ textAlign: 'right' }}>Value</th>
                <th>Measure</th>
                <th>Named in the extract</th>
              </tr>
            </thead>
            <tbody>
              {infra.map((r) => (
                <tr key={r.key}>
                  <td>{r.category}</td>
                  <td>{r.detail}</td>
                  <td className="num">{r.value}</td>
                  <td>{r.measure}</td>
                  <td style={{ color: 'var(--ink-text-dim)' }}>{r.named}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* CONTEXT: hazard split, districts, debris */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {hazardTotal > 0 && (
          <Card title="Area by hazard class" note="Depth–velocity classification (backend/analysis/hazard.py) on the run's maximum depth, velocity and depth × velocity." testid="card-hazard">
            <table className="w-full tabular-nums">
              <tbody>
                {[4, 3, 2, 1].map((c) => (
                  <tr key={c}>
                    <td className="py-0.5">
                      <span className="mr-2 inline-block h-2 w-2 rounded-[2px]" style={{ background: HAZARD[c] }} />
                      {HAZARD_LABELS[c]}
                    </td>
                    <td className="text-right font-mono">{n(data.hazard_km2?.[c] ?? 0, 2)} km²</td>
                  </tr>
                ))}
                {s.excluded_area_km2 > 0 && (
                  <tr className="text-[var(--ink-text-dim)]">
                    <td className="py-0.5">Excluded from depth statistics</td>
                    <td className="text-right font-mono">{n(s.excluded_area_km2, 2)} km²</td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>
        )}
        {data.districts && data.districts.length > 0 && (
          <Card title="Inundated area by district" note="District boundaries: OpenStreetMap administrative relations, admin_level 5, rasterised on the run grid." testid="card-districts">
            <table className="w-full tabular-nums">
              <tbody>
                {data.districts.map((d) => (
                  <tr key={d.osm_id}>
                    <td className="py-0.5">{d.name}</td>
                    <td className="text-right font-mono">{n(d.inundated_km2, 2)} km²</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
        {data.debris && (
          <Card
            title="Debris and sediment"
            note={`Derived from the team's flash-flood parameters: what their volumetric concentration implies for the plan's breach outflow, not a debris-flow simulation.${data.debris.method ? ` Their flow model: ${data.debris.method}.` : ''}`}
            testid="card-debris"
          >
            {data.debris.derived ? (
              <DebrisQuantities block={data.debris.derived} bare />
            ) : (
              data.debris.sediment && (
                <p className="mb-3 text-[var(--ink-text-dim)]">
                  The team's run has sediment transport switched {data.debris.sediment.on ? 'on' : 'off'} (Run Sediment={' '}
                  {data.debris.sediment.value}, {data.debris.sediment.source}).
                </p>
              )
            )}
          </Card>
        )}
      </div>

      {data.deep_ponding && (
        <section className="panel border-l-2" style={{ borderLeftColor: 'var(--ink-warn)' }} data-testid="data-quality">
          <div className="section-label mb-1">Data-quality finding · the team's HEC-RAS output</div>
          <p className="text-14 leading-relaxed">
            {data.deep_ponding.cells.toLocaleString('en-IN')} mesh cells ({n(data.deep_ponding.area_km2, 2)} km²) hold water more than 100 m above their bed
            {data.deep_ponding.max_apparent_depth_m !== null ? `, up to ${n(data.deep_ponding.max_apparent_depth_m, 0)} m` : ''}: {data.deep_ponding.rule}.
            {data.deep_ponding.void_cells_wet > 0 &&
              ` A further ${data.deep_ponding.void_cells_wet.toLocaleString('en-IN')} wet cells (${n(data.deep_ponding.void_area_km2, 2)} km²) sit over DEM voids, where the mesh bed is not ground.`}{' '}
            Neither is a flood depth. On the run grid these cells ({data.exclusion_rule}) count in the inundated area and are excluded from every depth,
            velocity and hazard statistic on this page.
          </p>
          <p className="mt-1 font-mono text-12 text-[var(--ink-text-faint)]">{data.deep_ponding.source}</p>
        </section>
      )}

      {data.settlements && data.settlements.length > 0 && (
        <section className="panel" style={{ padding: 0 }} data-testid="exposure-settlements">
          <header className="panel-header">
            <span className="panel-title" style={{ fontSize: 'var(--fs-body)', fontWeight: 600 }}>
              Settlements in the flood extent
            </span>
            <span className="section-label">sorted by arrival</span>
          </header>
          <div className="overflow-x-auto">
            <table className="w-full text-14 tabular-nums">
              <thead>
                <tr className="text-left text-12 text-[var(--ink-text-dim)]">
                  <th className="px-4 py-2 font-medium">Settlement</th>
                  <th className="px-4 py-2 font-medium">OSM place</th>
                  <th className="px-4 py-2 text-right font-medium">Arrival after breach</th>
                  <th className="px-4 py-2 text-right font-medium">Max depth (m)</th>
                  <th className="px-4 py-2 text-right font-medium">Max velocity (m/s)</th>
                  <th className="px-4 py-2 font-medium">Hazard</th>
                  {data.settlements.some((p) => p.population) && <th className="px-4 py-2 text-right font-medium">Population</th>}
                </tr>
              </thead>
              <tbody>
                {data.settlements.map((p) => (
                  <tr key={p.osm_id} className="border-t border-[var(--ink-border)]">
                    <td className="px-4 py-2">{p.name ?? `OSM node ${p.osm_id}`}</td>
                    <td className="px-4 py-2 text-[var(--ink-text-dim)]">{p.place}</td>
                    <td className="px-4 py-2 text-right font-mono">{p.arrival_min !== undefined ? hm(p.arrival_min) : ''}</td>
                    <td className="px-4 py-2 text-right font-mono">{p.max_depth_m !== undefined ? n(p.max_depth_m, 2) : ''}</td>
                    <td className="px-4 py-2 text-right font-mono">{p.max_velocity_ms !== undefined ? n(p.max_velocity_ms, 2) : ''}</td>
                    <td className="px-4 py-2">
                      {p.hazard_class ? (
                        <span className="flex items-center gap-2">
                          <span className="inline-block h-2 w-2 rounded-[2px]" style={{ background: HAZARD[p.hazard_class] }} />
                          {HAZARD_LABELS[p.hazard_class]}
                        </span>
                      ) : p.excluded ? (
                        <span className="text-[var(--ink-text-dim)]">data-quality cell</span>
                      ) : null}
                    </td>
                    {data.settlements!.some((q) => q.population) && (
                      <td className="px-4 py-2 text-right font-mono">{p.population ?? ''}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-[var(--ink-border)] px-4 py-2 text-12 text-[var(--ink-text-faint)]">
            Settlements: OSM place nodes on wet cells. Arrival: the run's arrival_time.tif.
            {data.settlements.some((p) => p.population) ? ' Population: OSM census:population tag.' : ' No census population is on file for these places.'}
          </div>
        </section>
      )}
    </div>
  )
}
